import { useState } from 'react';

function VoIPConfiguration() {
  /* ---------- VoIP configuration state ---------- */
  const [voipConfig, setVoipConfig] = useState({
    practice_id: '',
    main_number: '',
    voip_vendor: 'Yealink',
    voip_model: 'T54W',
    main_number_config: {
      extensions: [],
      use_all_extensions: true
    },
    users: [
      {
        name: 'NewtonPhone1',
        ext: '1',
        outbound_ani: '',
        mac: '',
        serial: '',
        is_softphone: false,
        vendor: 'Yealink',
        model: 'T54W',
        use_specific_model: false
      }
    ],
    additional_numbers: []
  });
  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backendServer, setBackendServer] = useState('great-civet-literally.ngrok-free.app');
  const [showTeledynamicsInput, setShowTeledynamicsInput] = useState(false);
  const [teledynamicsData, setTeledynamicsData] = useState('');

  /* ---------- VoIP configuration submission ---------- */
  const handleVoipSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!voipConfig.practice_id.trim() || !voipConfig.main_number.trim()) {
      setSubmitStatus('Please fill in practice ID and main number');
      return;
    }

    // Validate users
    for (let i = 0; i < voipConfig.users.length; i++) {
      const user = voipConfig.users[i];
      if (!user.name.trim()) {
        setSubmitStatus(`Please enter name for user ${i + 1}`);
        return;
      }
      if (!user.is_softphone) {
        // Validate MAC address
        const macStatus = getMacValidationStatus(user.mac, i);
        if (macStatus === 'empty') {
          setSubmitStatus(`Please enter MAC address for ${user.name} or select softphone`);
          return;
        }
        if (macStatus === 'invalid_length') {
          setSubmitStatus(`MAC address for ${user.name} must be exactly 12 characters long`);
          return;
        }
        if (macStatus === 'invalid_characters') {
          setSubmitStatus(`MAC address for ${user.name} must contain only hexadecimal characters (0-9, A-F, a-f)`);
          return;
        }
        if (macStatus === 'duplicate') {
          setSubmitStatus(`MAC address for ${user.name} must be unique across all MAC addresses and serial numbers`);
          return;
        }
        
        // Validate serial number
        const serialStatus = getSerialValidationStatus(user.serial, i);
        if (serialStatus === 'empty') {
          setSubmitStatus(`Please enter serial number for ${user.name} or select softphone`);
          return;
        }
        if (serialStatus === 'invalid_length') {
          setSubmitStatus(`Serial number for ${user.name} must be exactly 16 characters long`);
          return;
        }
        if (serialStatus === 'invalid_characters') {
          setSubmitStatus(`Serial number for ${user.name} must contain only alphanumeric characters`);
          return;
        }
        if (serialStatus === 'duplicate') {
          setSubmitStatus(`Serial number for ${user.name} must be unique across all MAC addresses and serial numbers`);
          return;
        }
      }
    }

    // Validate main number extensions
    const mainConfig = voipConfig.main_number_config;
    if (!mainConfig.use_all_extensions && (!mainConfig.extensions || mainConfig.extensions.length === 0)) {
      setSubmitStatus('Please select extensions for the main number');
      return;
    }

    // Validate additional phone numbers
    for (let i = 0; i < voipConfig.additional_numbers.length; i++) {
      const number = voipConfig.additional_numbers[i];
      if (!number.phone_number.trim()) {
        setSubmitStatus(`Please enter phone number for additional number ${i + 1}`);
        return;
      }
      if (!number.use_all_extensions && (!number.extensions || number.extensions.length === 0)) {
        setSubmitStatus(`Please select extensions for additional number ${i + 1}`);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus('Submitting configuration...');

    try {
      // Prepare data in the required format
      const numbersMapping = {
        [voipConfig.main_number]: getExtensionsString(voipConfig.main_number_config)
      };
      
      // Add additional numbers with their extension mappings
      voipConfig.additional_numbers.forEach(number => {
        if (number.phone_number.trim()) {
          numbersMapping[number.phone_number] = getExtensionsString(number);
        }
      });

      const configData = {
        practice_id: voipConfig.practice_id,
        main_number: voipConfig.main_number,
        voip_vendor: voipConfig.voip_vendor,
        voip_model: voipConfig.voip_model,
        users: voipConfig.users.map(user => {
          const userData = {
            name: user.name,
            ext: user.ext,
            outbound_ani: user.outbound_ani || voipConfig.main_number,
          };
          
          if (!user.is_softphone) {
            userData.mac = user.mac;
            userData.serial = user.serial;
            userData.vendor = user.vendor;
            userData.model = user.model;
          }
          
          return userData;
        }),
        numbers: numbersMapping
      };

      // Log the data being sent for debugging
      console.log('=== Sending VoIP Configuration to API ===');
      console.log(JSON.stringify(configData, null, 2));
      console.log('==========================================');
      
      // Submit to API
      const response = await fetch(`https://${backendServer}/api/provision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify(configData),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        // Format success message with details
        const stats = result.data?.statistics || {};
        const domain = result.data?.domain || '';
        
        let successMessage = `✅ ${result.message}\n\n`;
        if (domain) {
          successMessage += `🌐 Domain: ${domain}\n\n`;
        }
        successMessage += `📊 Summary:\n`;
        successMessage += `• ${stats.extensions_configured || 0} extensions configured\n`;
        successMessage += `• ${stats.added_devices || 0} devices added\n`;
        successMessage += `• ${stats.sip_trunks_created || 0} SIP trunks created`;
        
        setSubmitStatus(successMessage);
        console.log('✅ Provisioning Success:', result);
        
        // Log detailed information
        if (result.data?.details) {
          console.log('📱 Yealink Devices:', result.data.details.yealink_devices);
          console.log('📞 SIP Trunks:', result.data.details.sip_trunks);
        }
      } else {
        setSubmitStatus(`❌ Error: ${result.message || 'Failed to provision configuration'}`);
        console.error('❌ API Error:', result);
      }
      
    } catch (error) {
      setSubmitStatus(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVoipConfigChange = (field, value) => {
    setVoipConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Auto-update outbound_ani for all users when main_number changes
    if (field === 'main_number') {
      setVoipConfig(prev => ({
        ...prev,
        main_number: value,
        users: prev.users.map(user => ({
          ...user,
          outbound_ani: value
        }))
      }));
    }
    
    // Auto-update model for users that don't have specific models when voip_model changes
    if (field === 'voip_model') {
      setVoipConfig(prev => ({
        ...prev,
        voip_model: value,
        users: prev.users.map(user => ({
          ...user,
          model: user.use_specific_model ? user.model : value
        }))
      }));
    }
  };

  const handleUserChange = (index, field, value) => {
    setVoipConfig(prev => ({
      ...prev,
      users: prev.users.map((user, i) => {
        if (i === index) {
          const updatedUser = { ...user, [field]: value };
          
          // If use_specific_model is being unchecked, reset model to basic config model
          if (field === 'use_specific_model' && !value) {
            updatedUser.model = prev.voip_model;
          }
          
          return updatedUser;
        }
        return user;
      })
    }));
  };

  // Validation helper functions
  const validateMac = (mac) => {
    if (!mac.trim()) return 'empty';
    if (mac.length !== 12) return 'invalid_length';
    if (!/^[0-9A-Fa-f]{12}$/.test(mac)) return 'invalid_characters';
    return 'valid';
  };

  const validateSerial = (serial) => {
    if (!serial.trim()) return 'empty';
    if (serial.length !== 16) return 'invalid_length';
    if (!/^[0-9A-Za-z]+$/.test(serial)) return 'invalid_characters';
    return 'valid';
  };

  const getMacValidationStatus = (mac, currentIndex) => {
    // Check for duplicates first (highest precedence)
    if (mac.trim() !== '') {
      // Check for duplicate MAC addresses
      const duplicateMacIndex = voipConfig.users.findIndex((user, index) => 
        index !== currentIndex && user.mac === mac
      );
      if (duplicateMacIndex !== -1) return 'duplicate';
      
      // Check for duplicate serial numbers
      const duplicateSerialIndex = voipConfig.users.findIndex((user, index) => 
        user.serial === mac
      );
      if (duplicateSerialIndex !== -1) return 'duplicate';
    }
    
    const validation = validateMac(mac);
    if (validation === 'empty') return 'empty';
    if (validation === 'invalid_length') return 'invalid_length';
    if (validation === 'invalid_characters') return 'invalid_characters';
    
    return 'valid';
  };

  const getSerialValidationStatus = (serial, currentIndex) => {
    // Check for duplicates first (highest precedence)
    if (serial.trim() !== '') {
      // Check for duplicate serial numbers
      const duplicateSerialIndex = voipConfig.users.findIndex((user, index) => 
        index !== currentIndex && user.serial === serial
      );
      if (duplicateSerialIndex !== -1) return 'duplicate';
      
      // Check for duplicate MAC addresses
      const duplicateMacIndex = voipConfig.users.findIndex((user, index) => 
        user.mac === serial
      );
      if (duplicateMacIndex !== -1) return 'duplicate';
    }
    
    const validation = validateSerial(serial);
    if (validation === 'empty') return 'empty';
    if (validation === 'invalid_length') return 'invalid_length';
    if (validation === 'invalid_characters') return 'invalid_characters';
    
    return 'valid';
  };

  const getInputClassName = (status) => {
    switch (status) {
      case 'valid':
        return 'valid-input';
      case 'invalid_length':
        return 'invalid-length-input';
      case 'invalid_characters':
        return 'invalid-characters-input';
      case 'duplicate':
        return 'duplicate-input';
      default:
        return '';
    }
  };

  const addUser = () => {
    const newExt = (voipConfig.users.length + 1).toString();
    const newName = `NewtonPhone${newExt}`;
    setVoipConfig(prev => {
      const newUsers = [...prev.users, {
        name: newName,
        ext: newExt,
        outbound_ani: prev.main_number,
        mac: '',
        serial: '',
        is_softphone: false,
        vendor: 'Yealink',
        model: 'T54W',
        use_specific_model: false
      }];
      
      // Update main number extensions if "all extensions" is selected
      const updatedMainConfig = prev.main_number_config.use_all_extensions 
        ? { ...prev.main_number_config, extensions: newUsers.map(user => user.ext) }
        : prev.main_number_config;
      
      return {
        ...prev,
        users: newUsers,
        main_number_config: updatedMainConfig
      };
    });
  };

  const removeUser = (index) => {
    if (voipConfig.users.length > 1) {
      setVoipConfig(prev => {
        const newUsers = prev.users.filter((_, i) => i !== index).map((user, i) => ({
          ...user,
          name: `NewtonPhone${i + 1}`,
          ext: (i + 1).toString()
        }));
        
        // Update main number extensions if "all extensions" is selected
        const updatedMainConfig = prev.main_number_config.use_all_extensions 
          ? { ...prev.main_number_config, extensions: newUsers.map(user => user.ext) }
          : { 
              ...prev.main_number_config, 
              extensions: prev.main_number_config.extensions.filter(ext => 
                newUsers.some(user => user.ext === ext)
              )
            };
        
        return {
          ...prev,
          users: newUsers,
          main_number_config: updatedMainConfig
        };
      });
    }
  };

  const addAdditionalNumber = () => {
    setVoipConfig(prev => ({
      ...prev,
      additional_numbers: [...prev.additional_numbers, {
        phone_number: '',
        extensions: [],
        use_all_extensions: false
      }]
    }));
  };

  const removeAdditionalNumber = (index) => {
    setVoipConfig(prev => ({
      ...prev,
      additional_numbers: prev.additional_numbers.filter((_, i) => i !== index)
    }));
  };

  const handleAdditionalNumberChange = (index, field, value) => {
    setVoipConfig(prev => ({
      ...prev,
      additional_numbers: prev.additional_numbers.map((number, i) => 
        i === index ? { ...number, [field]: value } : number
      )
    }));
  };

  const toggleExtensionSelection = (numberIndex, extension) => {
    setVoipConfig(prev => ({
      ...prev,
      additional_numbers: prev.additional_numbers.map((number, i) => {
        if (i === numberIndex) {
          const currentExtensions = number.extensions || [];
          const isSelected = currentExtensions.includes(extension);
          
          let newExtensions;
          if (isSelected) {
            newExtensions = currentExtensions.filter(ext => ext !== extension);
          } else {
            newExtensions = [...currentExtensions, extension].sort((a, b) => parseInt(a) - parseInt(b));
          }
          
          return {
            ...number,
            extensions: newExtensions,
            use_all_extensions: false
          };
        }
        return number;
      })
    }));
  };

  const toggleAllExtensions = (numberIndex) => {
    setVoipConfig(prev => ({
      ...prev,
      additional_numbers: prev.additional_numbers.map((number, i) => {
        if (i === numberIndex) {
          const allExtensions = prev.users.map(user => user.ext);
          return {
            ...number,
            extensions: number.use_all_extensions ? [] : allExtensions,
            use_all_extensions: !number.use_all_extensions
          };
        }
        return number;
      })
    }));
  };

  // Helper functions for main number extension management
  const toggleMainNumberExtension = (extension) => {
    setVoipConfig(prev => ({
      ...prev,
      main_number_config: {
        ...prev.main_number_config,
        extensions: prev.main_number_config.extensions.includes(extension)
          ? prev.main_number_config.extensions.filter(ext => ext !== extension)
          : [...prev.main_number_config.extensions, extension].sort((a, b) => parseInt(a) - parseInt(b)),
        use_all_extensions: false
      }
    }));
  };

  const toggleMainNumberAllExtensions = () => {
    setVoipConfig(prev => ({
      ...prev,
      main_number_config: {
        ...prev.main_number_config,
        extensions: prev.main_number_config.use_all_extensions ? [] : prev.users.map(user => user.ext),
        use_all_extensions: !prev.main_number_config.use_all_extensions
      }
    }));
  };

  const getExtensionsString = (number) => {
    if (number.use_all_extensions) {
      return voipConfig.users.map(user => user.ext).join(" ");
    }
    return number.extensions.join(" ");
  };

  const handlePopulateTeledynamics = () => {
    if (!teledynamicsData.trim()) {
      setSubmitStatus('Please enter Teledynamics data');
      return;
    }

    try {
      const lines = teledynamicsData.trim().split('\n');
      
      // Skip the first line (header) and process the rest
      const dataLines = lines.slice(1).filter(line => line.trim());
      
      if (dataLines.length === 0) {
        setSubmitStatus('No valid data found. Please check the format.');
        return;
      }

      // Create new users based on the data
      const newUsers = dataLines.map((line, index) => {
        const parts = line.trim().split(/\s+/);
        if (parts.length < 2) {
          throw new Error(`Invalid format on line ${index + 2}: ${line}`);
        }
        
        const serial = parts[0];
        const mac = parts[1];
        
        // Validate MAC address format (12 hexadecimal characters)
        if (!/^[0-9A-Fa-f]{12}$/.test(mac)) {
          throw new Error(`Invalid MAC address format on line ${index + 2}: ${mac}`);
        }
        
        // Validate serial number format (alphanumeric, 16 characters)
        if (!/^[0-9A-Za-z]{16}$/.test(serial)) {
          throw new Error(`Invalid serial number format on line ${index + 2}: ${serial}`);
        }
        
        return {
          name: `NewtonPhone${index + 1}`,
          ext: (index + 1).toString(),
          outbound_ani: voipConfig.main_number,
          mac: mac.toUpperCase(),
          serial: serial.toUpperCase(),
          is_softphone: false,
          vendor: 'Yealink',
          model: voipConfig.voip_model,
          use_specific_model: false
        };
      });

      // Update the configuration with new users
      setVoipConfig(prev => ({
        ...prev,
        users: newUsers,
        main_number_config: {
          ...prev.main_number_config,
          extensions: newUsers.map(user => user.ext),
          use_all_extensions: true
        }
      }));

      setSubmitStatus(`✅ Successfully populated ${newUsers.length} users from Teledynamics data`);
      
    } catch (error) {
      setSubmitStatus(`❌ Error parsing Teledynamics data: ${error.message}`);
    }
  };

  return (
    <div className="tab-content">
      <h1>VoIP Configuration</h1>
      <form onSubmit={handleVoipSubmit} className="voip-config-form">
        
        {/* Backend Server Configuration */}
        <div className="config-section">
          <h2>Backend Server Configuration</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="backend_server">Backend Provisioning Server:</label>
              <input
                type="text"
                id="backend_server"
                value={backendServer}
                onChange={(e) => setBackendServer(e.target.value)}
                placeholder="great-civet-literally.ngrok-free.app"
                required
              />
            </div>
          </div>
          <div className="server-recommendations">
            <h3>Recommended Servers:</h3>
            <ul>
              <li 
                className="clickable-server"
                onClick={() => setBackendServer('great-civet-literally.ngrok-free.app')}
              >
                great-civet-literally.ngrok-free.app
              </li>
              <li 
                className="clickable-server"
                onClick={() => setBackendServer('newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net')}
              >
                newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net
              </li>
            </ul>
          </div>
        </div>
        
        {/* Basic Configuration */}
        <div className="config-section">
          <h2>Basic Configuration</h2>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="practice_id">Practice ID:</label>
              <input
                type="text"
                id="practice_id"
                value={voipConfig.practice_id}
                onChange={(e) => handleVoipConfigChange('practice_id', e.target.value)}
                placeholder="practice-1234567890"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="main_number">Main Number:</label>
              <input
                type="tel"
                id="main_number"
                value={voipConfig.main_number}
                onChange={(e) => handleVoipConfigChange('main_number', e.target.value)}
                placeholder="+16457771667"
                required
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="voip_vendor">VoIP Vendor:</label>
              <input
                type="text"
                id="voip_vendor"
                value={voipConfig.voip_vendor}
                readOnly
                className="readonly"
              />
            </div>
            <div className="form-group">
              <label htmlFor="voip_model">VoIP Model:</label>
              <select
                id="voip_model"
                value={voipConfig.voip_model}
                onChange={(e) => handleVoipConfigChange('voip_model', e.target.value)}
                required
              >
                <option value="T54W">T54W</option>
                <option value="T57W">T57W</option>
                <option value="T46S">T46S</option>
                <option value="T46U">T46U</option>
                <option value="W60B">W60B</option>
                <option value="W70B">W70B</option>
                <option value="T33G">T33G</option>
                <option value="T42S">T42S</option>
              </select>
            </div>
          </div>
          
          {/* Teledynamics Population */}
          <div className="form-group">
            <div className="checkbox-container">
              <input
                type="checkbox"
                id="populate_teledynamics"
                checked={showTeledynamicsInput}
                onChange={(e) => setShowTeledynamicsInput(e.target.checked)}
              />
              <label htmlFor="populate_teledynamics">Populate with Teledynamics info</label>
            </div>
          </div>
          
          {showTeledynamicsInput && (
            <div className="form-group">
              <label htmlFor="teledynamics_data">Teledynamics Data:</label>
              <textarea
                id="teledynamics_data"
                value={teledynamicsData}
                onChange={(e) => setTeledynamicsData(e.target.value)}
                placeholder="Serial Number	Mac Address&#10;201081E061200386 805E0CDC73D3&#10;201081E061200387 805E0CDC73D4"
                rows={6}
                style={{ fontFamily: 'monospace', fontSize: '14px' }}
              />
              <small className="help-text">
                Enter data in the format: Serial Number [tab] Mac Address (one per line). The first line (header) will be ignored.
              </small>
              <div style={{ marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={handlePopulateTeledynamics}
                  className="add-user-btn"
                  style={{ marginRight: '10px' }}
                >
                  Populate Form
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTeledynamicsData('');
                    setShowTeledynamicsInput(false);
                  }}
                  className="remove-user-btn"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Users Configuration */}
        <div className="config-section">
          <div className="section-header">
            <h2>Users Configuration</h2>
            <button 
              type="button" 
              onClick={addUser}
              className="add-user-btn"
            >
              Add User
            </button>
          </div>
          
          {voipConfig.users.map((user, index) => (
            <div key={index} className="user-config">
              <div className="user-header">
                <h3>User {index + 1}</h3>
                {voipConfig.users.length > 1 && (
                  <button 
                    type="button"
                    onClick={() => removeUser(index)}
                    className="remove-user-btn"
                  >
                    Remove
                  </button>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`user_name_${index}`}>Name:</label>
                  <input
                    type="text"
                    id={`user_name_${index}`}
                    value={user.name}
                    onChange={(e) => handleUserChange(index, 'name', e.target.value)}
                    placeholder={`NewtonPhone${index + 1}`}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`user_ext_${index}`}>Extension:</label>
                  <input
                    type="text"
                    id={`user_ext_${index}`}
                    value={user.ext}
                    onChange={(e) => handleUserChange(index, 'ext', e.target.value)}
                    placeholder={`${index + 1}`}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id={`specific_model_${index}`}
                    checked={user.use_specific_model}
                    onChange={(e) => handleUserChange(index, 'use_specific_model', e.target.checked)}
                  />
                  <label htmlFor={`specific_model_${index}`}>Select specific model</label>
                </div>
              </div>
              
              {user.use_specific_model && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`user_model_${index}`}>Model:</label>
                    <select
                      id={`user_model_${index}`}
                      value={user.model}
                      onChange={(e) => handleUserChange(index, 'model', e.target.value)}
                      required
                    >
                      <option value="T54W">T54W</option>
                      <option value="T57W">T57W</option>
                      <option value="T46S">T46S</option>
                      <option value="T46U">T46U</option>
                      <option value="W60B">W60B</option>
                      <option value="W70B">W70B</option>
                      <option value="T33G">T33G</option>
                      <option value="T42S">T42S</option>
                    </select>
                  </div>
                </div>
              )}
              
              {!user.use_specific_model && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Model:</label>
                    <input
                      type="text"
                      value={`${user.model} (from basic configuration)`}
                      readOnly
                      className="readonly"
                    />
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <div className="checkbox-container">
                  <input
                    type="checkbox"
                    id={`softphone_${index}`}
                    checked={user.is_softphone}
                    onChange={(e) => handleUserChange(index, 'is_softphone', e.target.checked)}
                  />
                  <label htmlFor={`softphone_${index}`}>Softphone (no physical device)</label>
                </div>
              </div>
              
                                {!user.is_softphone && (
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`user_mac_${index}`}>MAC Address:</label>
                        <input
                          type="text"
                          id={`user_mac_${index}`}
                          value={user.mac}
                          onChange={(e) => handleUserChange(index, 'mac', e.target.value)}
                          placeholder="805E0CDC73D3"
                          required={!user.is_softphone}
                          className={getInputClassName(getMacValidationStatus(user.mac, index))}
                        />
                        <small className="help-text">
                          Must be exactly 12 hexadecimal characters (0-9, A-F)
                          {user.mac.length > 0 && (
                            <span className={getMacValidationStatus(user.mac, index) === 'valid' ? 'valid' : 'invalid'}>
                              {user.mac.length}/12 characters
                              {getMacValidationStatus(user.mac, index) === 'duplicate' && ' (Duplicate)'}
                            </span>
                          )}
                        </small>
                      </div>
                      <div className="form-group">
                        <label htmlFor={`user_serial_${index}`}>Serial Number:</label>
                        <input
                          type="text"
                          id={`user_serial_${index}`}
                          value={user.serial}
                          onChange={(e) => handleUserChange(index, 'serial', e.target.value)}
                          placeholder="201081E061200386"
                          required={!user.is_softphone}
                          className={getInputClassName(getSerialValidationStatus(user.serial, index))}
                        />
                        <small className="help-text">
                          Alphanumeric characters only (0-9, A-Z, a-z)
                          {user.serial.length > 0 && (
                            <span className={getSerialValidationStatus(user.serial, index) === 'valid' ? 'valid' : 'invalid'}>
                              {user.serial.length}/16 characters
                              {getSerialValidationStatus(user.serial, index) === 'duplicate' && ' (Duplicate)'}
                            </span>
                          )}
                        </small>
                      </div>
                    </div>
                  )}
            </div>
          ))}
        </div>

        {/* Phone Numbers Configuration */}
        <div className="config-section">
          <div className="section-header">
            <h2>Phone Numbers Configuration</h2>
            <button 
              type="button" 
              onClick={addAdditionalNumber}
              className="add-user-btn"
            >
              Add Number
            </button>
          </div>
          
          {/* Main Number Display */}
          <div className="number-config main-number">
            <div className="number-header">
              <h3>Main Number</h3>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Phone Number:</label>
                <input
                  type="text"
                  value={voipConfig.main_number || 'Not set'}
                  readOnly
                  className="readonly"
                />
              </div>
              <div className="form-group">
                <label>Extensions:</label>
                <div className="extensions-selection">
                  <div className="extension-option">
                    <input
                      type="checkbox"
                      id="main_all_ext"
                      checked={voipConfig.main_number_config.use_all_extensions}
                      onChange={toggleMainNumberAllExtensions}
                    />
                    <label htmlFor="main_all_ext">All Extensions</label>
                  </div>
                  
                  {!voipConfig.main_number_config.use_all_extensions && (
                    <div className="individual-extensions">
                      <div className="extensions-grid">
                        {voipConfig.users.map((user) => (
                          <div key={user.ext} className="extension-option">
                            <input
                              type="checkbox"
                              id={`main_ext_${user.ext}`}
                              checked={(voipConfig.main_number_config.extensions || []).includes(user.ext)}
                              onChange={() => toggleMainNumberExtension(user.ext)}
                            />
                            <label htmlFor={`main_ext_${user.ext}`}>
                              Ext {user.ext} ({user.name})
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="help-text">
              <small>
                Select which extensions can receive calls from this number. Choose "All Extensions" or select specific extensions.
              </small>
            </div>
          </div>

          {/* Additional Numbers */}
          {voipConfig.additional_numbers.map((number, index) => (
            <div key={index} className="number-config">
              <div className="number-header">
                <h3>Additional Number {index + 1}</h3>
                <button 
                  type="button"
                  onClick={() => removeAdditionalNumber(index)}
                  className="remove-user-btn"
                >
                  Remove
                </button>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor={`number_phone_${index}`}>Phone Number:</label>
                  <input
                    type="tel"
                    id={`number_phone_${index}`}
                    value={number.phone_number}
                    onChange={(e) => handleAdditionalNumberChange(index, 'phone_number', e.target.value)}
                    placeholder="+16457771067"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Extensions:</label>
                  <div className="extensions-selection">
                    <div className="extension-option">
                      <input
                        type="checkbox"
                        id={`all_ext_${index}`}
                        checked={number.use_all_extensions}
                        onChange={() => toggleAllExtensions(index)}
                      />
                      <label htmlFor={`all_ext_${index}`}>All Extensions</label>
                    </div>
                    
                    {!number.use_all_extensions && (
                      <div className="individual-extensions">
                        <div className="extensions-grid">
                          {voipConfig.users.map((user) => (
                            <div key={user.ext} className="extension-option">
                              <input
                                type="checkbox"
                                id={`ext_${index}_${user.ext}`}
                                checked={(number.extensions || []).includes(user.ext)}
                                onChange={() => toggleExtensionSelection(index, user.ext)}
                              />
                              <label htmlFor={`ext_${index}_${user.ext}`}>
                                Ext {user.ext} ({user.name})
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="help-text">
                <small>
                  Select which extensions can receive calls from this number. Choose "All Extensions" or select specific extensions.
                </small>
              </div>
            </div>
          ))}
          
          {voipConfig.additional_numbers.length === 0 && (
            <div className="no-additional-numbers">
              <p>No additional numbers configured. Click "Add Number" to add more phone numbers.</p>
            </div>
          )}
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting Configuration...' : 'Submit Configuration'}
        </button>

        {submitStatus && (
          <div className={`status-message ${submitStatus.includes('Error') ? 'error' : 'success'}`}>
            {submitStatus}
          </div>
        )}
      </form>
    </div>
  );
}

export default VoIPConfiguration; 